import { appendQualitySlide } from './reportQualityExport';
// Generate PowerPoint report for Digital Maturity Assessment
import pptxgen from 'pptxgenjs';

export const generateMaturityPptx = (results, fullResults) => {
  const pptx = new pptxgen();
  
  // Set presentation properties
  pptx.author = 'SAMANVAI';
  pptx.title = `Digital Maturity Assessment - ${results.company_name}`;
  pptx.subject = 'Digital Transformation Readiness Report';
  
  // Color scheme
  const colors = {
    primary: '059669',    // Emerald
    secondary: '0891B2',  // Cyan
    dark: '1F2937',       // Gray 800
    darkGray: '374151',
    mediumGray: '6B7280',
    light: 'F3F4F6',      // Gray 100
    white: 'FFFFFF',
    red: 'EF4444',
    orange: 'F97316',
    yellow: 'EAB308',
    green: '22C55E'
  };
  
  const getScoreColor = (score) => {
    if (score < 2) return colors.red;
    if (score < 3) return colors.orange;
    if (score < 4) return colors.yellow;
    return colors.green;
  };

  const data = fullResults || results;
  const overallScore = data.overall_score || results.overall_score || 2.0;
  const dimensions = data.dimension_scores || results.dimension_scores || [];
  const benchmark = data.benchmark_comparison || results.benchmark_comparison || { industry_average: '3.0', top_performers: '4.5' };

  // Helper function to find matching analysis for a dimension
  const findDimensionAnalysis = (dimName, dimId) => {
    const aiAnalysis = data.ai_analysis?.dimension_analysis || data.dimension_analysis || [];
    if (!Array.isArray(aiAnalysis) || aiAnalysis.length === 0) return null;
    
    return aiAnalysis.find(a => {
      if (!a) return false;
      if (dimId && (a.dimension_id === dimId || a.id === dimId)) return true;
      if (a.dimension === dimName || a.name === dimName) return true;
      const searchTerm = dimName.toLowerCase().split(' ')[0];
      const analysisName = (a.dimension || a.name || '').toLowerCase();
      return analysisName.includes(searchTerm) || searchTerm.includes(analysisName.split(' ')[0]);
    });
  };

  // Digital maturity-specific recommendations based on dimension and score
  const getDigitalSpecificRecommendation = (dimName, score, analysis) => {
    if (analysis) {
      const recs = analysis.quick_wins || analysis.recommendations || analysis.improvement_areas || [];
      if (Array.isArray(recs) && recs.length > 0) {
        return typeof recs[0] === 'string' ? recs[0] : recs[0].recommendation || recs[0].action || String(recs[0]);
      }
    }
    
    const dimLower = dimName.toLowerCase();
    if (score < 2) {
      if (dimLower.includes('strategy') || dimLower.includes('vision')) 
        return 'CRITICAL: Define comprehensive digital strategy with clear business outcomes and executive sponsorship';
      if (dimLower.includes('customer') || dimLower.includes('experience')) 
        return 'URGENT: Map customer journeys and identify digital touchpoint opportunities';
      if (dimLower.includes('operation') || dimLower.includes('process')) 
        return 'PRIORITY: Identify high-impact processes for automation and digitization';
      if (dimLower.includes('technology') || dimLower.includes('infrastructure')) 
        return 'IMMEDIATE: Assess technology stack and develop cloud migration roadmap';
      if (dimLower.includes('data') || dimLower.includes('analytics')) 
        return 'FOUNDATIONAL: Establish data governance and implement business intelligence capabilities';
      if (dimLower.includes('culture') || dimLower.includes('people') || dimLower.includes('talent')) 
        return 'CRITICAL: Launch digital skills assessment and develop comprehensive training programs';
      if (dimLower.includes('innovation')) 
        return 'PRIORITY: Establish innovation process and pilot emerging technology experiments';
    } else if (score < 3) {
      if (dimLower.includes('strategy') || dimLower.includes('vision')) 
        return 'Align digital initiatives with business metrics and establish transformation governance';
      if (dimLower.includes('customer') || dimLower.includes('experience')) 
        return 'Implement omnichannel engagement platform with personalization capabilities';
      if (dimLower.includes('operation') || dimLower.includes('process')) 
        return 'Scale process automation with RPA and implement intelligent workflow management';
      if (dimLower.includes('technology') || dimLower.includes('infrastructure')) 
        return 'Modernize architecture with microservices and implement DevOps practices';
      if (dimLower.includes('data') || dimLower.includes('analytics')) 
        return 'Deploy advanced analytics with predictive capabilities and real-time dashboards';
      if (dimLower.includes('culture') || dimLower.includes('people') || dimLower.includes('talent')) 
        return 'Scale digital upskilling across organization and establish centers of excellence';
      if (dimLower.includes('innovation')) 
        return 'Establish innovation lab and implement structured ideation-to-pilot pipeline';
    } else {
      if (dimLower.includes('strategy') || dimLower.includes('vision')) 
        return 'Achieve digital-first business model with continuous strategy evolution';
      if (dimLower.includes('customer') || dimLower.includes('experience')) 
        return 'Pioneer AI-powered hyper-personalization and predictive customer engagement';
      if (dimLower.includes('operation') || dimLower.includes('process')) 
        return 'Achieve intelligent operations with autonomous optimization';
      if (dimLower.includes('technology') || dimLower.includes('infrastructure')) 
        return 'Lead with composable architecture and edge computing capabilities';
      if (dimLower.includes('data') || dimLower.includes('analytics')) 
        return 'Pioneer AI-driven decision intelligence and democratized data access';
      if (dimLower.includes('culture') || dimLower.includes('people') || dimLower.includes('talent')) 
        return 'Achieve digital-native culture with continuous learning and innovation mindset';
      if (dimLower.includes('innovation')) 
        return 'Lead industry disruption with strategic technology investments';
    }
    return `Strengthen ${dimName.toLowerCase()} capabilities through targeted digital initiatives`;
  };

  // Score-based roadmap phases for digital maturity
  const getDigitalRoadmap = (score) => {
    if (score < 2) {
      return [
        {
          phase: 'Foundation',
          duration: 'Months 1-3',
          focus_areas: ['Digital strategy definition', 'Current state assessment', 'Quick wins identification'],
          initiatives: ['Define digital vision and roadmap', 'Assess technology infrastructure', 'Launch first automation pilots', 'Establish digital governance'],
          investment_range: '$100K - $250K'
        },
        {
          phase: 'Build',
          duration: 'Months 4-6',
          focus_areas: ['Technology modernization', 'Process digitization', 'Skill development'],
          initiatives: ['Begin cloud migration', 'Digitize priority processes', 'Launch digital training programs', 'Deploy analytics foundation'],
          investment_range: '$200K - $400K'
        },
        {
          phase: 'Scale',
          duration: 'Months 7-9',
          focus_areas: ['Platform expansion', 'Customer experience', 'Operational excellence'],
          initiatives: ['Expand digital platforms', 'Launch customer digital channels', 'Scale process automation', 'Build data capabilities'],
          investment_range: '$250K - $500K'
        },
        {
          phase: 'Optimize',
          duration: 'Months 10-12',
          focus_areas: ['Advanced capabilities', 'Innovation culture', 'Continuous improvement'],
          initiatives: ['Deploy advanced analytics', 'Establish innovation programs', 'Measure digital ROI', 'Plan next phase'],
          investment_range: '$150K - $300K'
        }
      ];
    } else if (score < 3) {
      return [
        {
          phase: 'Accelerate',
          duration: 'Months 1-3',
          focus_areas: ['Strategy alignment', 'Platform optimization', 'Advanced automation'],
          initiatives: ['Refine digital strategy with KPIs', 'Optimize digital platforms', 'Deploy intelligent automation', 'Enhance data governance'],
          investment_range: '$150K - $300K'
        },
        {
          phase: 'Industrialize',
          duration: 'Months 4-6',
          focus_areas: ['Enterprise scale', 'Customer intelligence', 'DevOps maturity'],
          initiatives: ['Scale digital across enterprise', 'Implement AI/ML capabilities', 'Achieve DevOps excellence', 'Launch innovation lab'],
          investment_range: '$250K - $500K'
        },
        {
          phase: 'Innovate',
          duration: 'Months 7-9',
          focus_areas: ['Emerging technologies', 'Digital products', 'Ecosystem partnerships'],
          initiatives: ['Pilot emerging technologies', 'Launch digital products/services', 'Build ecosystem partnerships', 'Achieve omnichannel excellence'],
          investment_range: '$300K - $600K'
        },
        {
          phase: 'Transform',
          duration: 'Months 10-12',
          focus_areas: ['Business model innovation', 'Digital leadership', 'Continuous evolution'],
          initiatives: ['Evolve business model', 'Achieve industry recognition', 'Pioneer new capabilities', 'Plan strategic investments'],
          investment_range: '$200K - $400K'
        }
      ];
    }
    return [
      {
        phase: 'Lead',
        duration: 'Months 1-3',
        focus_areas: ['Competitive advantage', 'AI-first operations', 'Digital ecosystem'],
        initiatives: ['Deploy AI at scale', 'Achieve autonomous operations', 'Lead ecosystem development', 'Pioneer industry standards'],
        investment_range: '$200K - $400K'
      },
      {
        phase: 'Pioneer',
        duration: 'Months 4-6',
        focus_areas: ['Technology leadership', 'Market disruption', 'Talent excellence'],
        initiatives: ['Invest in frontier technologies', 'Launch disruptive offerings', 'Build world-class digital team', 'Shape industry direction'],
        investment_range: '$350K - $700K'
      },
      {
        phase: 'Dominate',
        duration: 'Months 7-9',
        focus_areas: ['Platform business', 'Global scale', 'Strategic partnerships'],
        initiatives: ['Monetize digital platforms', 'Scale globally', 'Build strategic alliances', 'Acquire digital capabilities'],
        investment_range: '$400K - $800K'
      },
      {
        phase: 'Transcend',
        duration: 'Months 10-12',
        focus_areas: ['Future readiness', 'Sustainable growth', 'Legacy building'],
        initiatives: ['Prepare for next disruption', 'Ensure sustainable growth', 'Build lasting digital legacy', 'Shape future industry'],
        investment_range: '$300K - $600K'
      }
    ];
  };

  // ============== Slide 1: Title Slide ==============
  const slide1 = pptx.addSlide();
  slide1.background = { color: colors.dark };
  
  // Logo/Brand
  slide1.addText('SAMANVAI', {
    x: 0.5, y: 0.4, w: 3, h: 0.5,
    fontSize: 24, bold: true, color: colors.primary
  });
  
  // Main title
  slide1.addText('Digital Maturity Assessment', {
    x: 0.5, y: 2, w: 9, h: 1,
    fontSize: 44, bold: true, color: colors.white
  });
  
  // Company name
  slide1.addText(results.company_name, {
    x: 0.5, y: 3.1, w: 9, h: 0.6,
    fontSize: 28, color: colors.secondary
  });
  
  // Meta info
  slide1.addText(`${results.industry} | ${results.company_size} employees | ${results.annual_revenue}`, {
    x: 0.5, y: 3.8, w: 9, h: 0.4,
    fontSize: 14, color: 'A0AEC0'
  });
  
  // Date
  slide1.addText(`Assessment Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, {
    x: 0.5, y: 4.8, w: 5, h: 0.3,
    fontSize: 12, color: '718096'
  });
  
  // Overall Score
  slide1.addShape('rect', {
    x: 7.5, y: 1.8, w: 2, h: 2.5,
    fill: { color: getScoreColor(results.overall_score) },
    line: { color: colors.white, width: 2 }
  });
  slide1.addText(results.overall_score.toFixed(1), {
    x: 7.5, y: 2.1, w: 2, h: 1,
    fontSize: 48, bold: true, color: colors.white, align: 'center'
  });
  slide1.addText('out of 5.0', {
    x: 7.5, y: 3, w: 2, h: 0.3,
    fontSize: 11, color: colors.white, align: 'center'
  });
  slide1.addText(results.overall_maturity_level, {
    x: 7.5, y: 3.4, w: 2, h: 0.4,
    fontSize: 12, bold: true, color: colors.white, align: 'center'
  });

  // ============== Slide 2: Executive Summary ==============
  const slide2 = pptx.addSlide();
  slide2.addText('Executive Summary', {
    x: 0.5, y: 0.3, w: 9, h: 0.6,
    fontSize: 28, bold: true, color: colors.dark
  });
  
  // Maturity description box
  slide2.addShape('rect', {
    x: 0.5, y: 1, w: 9, h: 0.8,
    fill: { color: colors.light }
  });
  slide2.addText(`${results.overall_maturity_level}: ${results.overall_maturity_description}`, {
    x: 0.6, y: 1.1, w: 8.8, h: 0.6,
    fontSize: 12, color: colors.dark
  });
  
  // Executive summary text
  const summaryText = results.executive_summary || 'No executive summary available.';
  slide2.addText(summaryText, {
    x: 0.5, y: 2, w: 9, h: 3,
    fontSize: 11, color: colors.dark, valign: 'top'
  });

  // ============== Slide 3: Dimension Scores ==============
  const slide3 = pptx.addSlide();
  slide3.addText('Digital Maturity by Dimension', {
    x: 0.5, y: 0.3, w: 9, h: 0.6,
    fontSize: 28, bold: true, color: colors.dark
  });
  
  // Create dimension score cards
  dimensions.forEach((dim, idx) => {
    const col = idx % 3;
    const row = Math.floor(idx / 3);
    const x = 0.5 + col * 3.2;
    const y = 1.1 + row * 2.2;
    
    // Card background
    slide3.addShape('rect', {
      x: x, y: y, w: 3, h: 2,
      fill: { color: colors.white },
      line: { color: colors.light, width: 1 }
    });
    
    // Score circle
    slide3.addShape('ellipse', {
      x: x + 0.2, y: y + 0.3, w: 0.8, h: 0.8,
      fill: { color: getScoreColor(dim.score) }
    });
    slide3.addText(dim.score.toFixed(1), {
      x: x + 0.2, y: y + 0.45, w: 0.8, h: 0.5,
      fontSize: 14, bold: true, color: colors.white, align: 'center'
    });
    
    // Dimension name
    slide3.addText(dim.dimension_name, {
      x: x + 1.1, y: y + 0.3, w: 1.8, h: 0.5,
      fontSize: 11, bold: true, color: colors.dark
    });
    
    // Maturity level
    slide3.addText(dim.maturity_level, {
      x: x + 1.1, y: y + 0.7, w: 1.8, h: 0.3,
      fontSize: 9, color: getScoreColor(dim.score)
    });
    
    // Progress bar
    const barWidth = 2.6;
    slide3.addShape('rect', {
      x: x + 0.2, y: y + 1.3, w: barWidth, h: 0.15,
      fill: { color: colors.light }
    });
    slide3.addShape('rect', {
      x: x + 0.2, y: y + 1.3, w: barWidth * (dim.percentage / 100), h: 0.15,
      fill: { color: getScoreColor(dim.score) }
    });
    
    // Percentage
    slide3.addText(`${dim.percentage}%`, {
      x: x + 0.2, y: y + 1.5, w: 2.6, h: 0.3,
      fontSize: 9, color: '718096', align: 'right'
    });
  });

  // ============== Slide 3b: Detailed Dimension Analysis ==============
  const slide3b = pptx.addSlide();
  slide3b.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.secondary }
  });
  slide3b.addText('Detailed Dimension Analysis', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  const firstSixDims = dimensions.slice(0, 6);
  firstSixDims.forEach((dim, idx) => {
    const y = 1.15 + idx * 0.85;
    const score = dim.score || 0;
    const dimName = dim.dimension_name || dim.name || `Dimension ${idx + 1}`;
    const dimId = dim.dimension_id || dim.id;
    
    const analysis = findDimensionAnalysis(dimName, dimId);
    
    // Dimension header
    slide3b.addText(`${dimName}: ${score.toFixed(1)}/5.0`, {
      x: 0.5, y: y, w: 4.5, h: 0.32,
      fontSize: 11, bold: true, color: colors.dark
    });
    
    // Score bar
    slide3b.addShape('rect', {
      x: 5.5, y: y + 0.03, w: 4, h: 0.22,
      fill: { color: colors.light }
    });
    slide3b.addShape('rect', {
      x: 5.5, y: y + 0.03, w: (score / 5) * 4, h: 0.22,
      fill: { color: getScoreColor(score) }
    });
    
    // Key recommendation - specific to Digital Maturity
    const keyRec = getDigitalSpecificRecommendation(dimName, score, analysis);
    
    slide3b.addText(`→ ${keyRec}`, {
      x: 0.5, y: y + 0.35, w: 9, h: 0.45,
      fontSize: 9, color: colors.mediumGray, wrap: true
    });
  });

  // ============== Slide 4: Key Findings ==============
  const slide4 = pptx.addSlide();
  slide4.addText('Key Findings', {
    x: 0.5, y: 0.3, w: 9, h: 0.6,
    fontSize: 28, bold: true, color: colors.dark
  });
  
  const findings = results.key_findings || [];
  findings.forEach((finding, idx) => {
    slide4.addText('✓', {
      x: 0.5, y: 1 + idx * 0.6, w: 0.4, h: 0.5,
      fontSize: 14, color: colors.primary
    });
    slide4.addText(finding, {
      x: 1, y: 1 + idx * 0.6, w: 8.5, h: 0.5,
      fontSize: 11, color: colors.dark
    });
  });

  // ============== Slide 5: Quick Wins ==============
  const slide5 = pptx.addSlide();
  slide5.addText('Quick Wins', {
    x: 0.5, y: 0.3, w: 9, h: 0.6,
    fontSize: 28, bold: true, color: colors.dark
  });
  slide5.addText('High-impact, low-effort initiatives to start immediately', {
    x: 0.5, y: 0.8, w: 9, h: 0.4,
    fontSize: 12, color: '718096'
  });
  
  // Generate score-specific quick wins if not provided
  const generateQuickWins = () => {
    if (overallScore < 2) {
      return [
        { initiative: 'Process digitization audit', description: 'Identify top 5 manual processes for automation', effort: 'Low', impact: 'High', timeline: '2-4 weeks' },
        { initiative: 'Cloud assessment', description: 'Evaluate infrastructure for cloud migration readiness', effort: 'Low', impact: 'High', timeline: '2-4 weeks' },
        { initiative: 'Digital skills inventory', description: 'Assess current team capabilities and gaps', effort: 'Low', impact: 'Medium', timeline: '2-3 weeks' },
        { initiative: 'Customer journey mapping', description: 'Document current customer touchpoints', effort: 'Medium', impact: 'High', timeline: '4-6 weeks' },
        { initiative: 'RPA pilot selection', description: 'Select first process for automation pilot', effort: 'Low', impact: 'High', timeline: '2-4 weeks' }
      ];
    } else if (overallScore < 3) {
      return [
        { initiative: 'AI use case identification', description: 'Identify 3 high-value AI/ML applications', effort: 'Low', impact: 'High', timeline: '2-4 weeks' },
        { initiative: 'DevOps assessment', description: 'Evaluate CI/CD pipeline maturity', effort: 'Low', impact: 'Medium', timeline: '2-3 weeks' },
        { initiative: 'Data quality audit', description: 'Assess data quality for key business domains', effort: 'Medium', impact: 'High', timeline: '4-6 weeks' },
        { initiative: 'Customer analytics pilot', description: 'Implement predictive customer analytics', effort: 'Medium', impact: 'High', timeline: '6-8 weeks' },
        { initiative: 'Innovation workshop', description: 'Conduct design thinking innovation session', effort: 'Low', impact: 'Medium', timeline: '1-2 weeks' }
      ];
    }
    return [
      { initiative: 'Edge computing pilot', description: 'Deploy edge AI for real-time processing', effort: 'Medium', impact: 'High', timeline: '8-12 weeks' },
      { initiative: 'Platform API monetization', description: 'Launch API marketplace for partners', effort: 'Medium', impact: 'High', timeline: '8-12 weeks' },
      { initiative: 'Digital product launch', description: 'Fast-track new digital product offering', effort: 'Medium', impact: 'High', timeline: '8-12 weeks' },
      { initiative: 'Ecosystem partnership', description: 'Establish strategic digital partnership', effort: 'Low', impact: 'High', timeline: '4-8 weeks' },
      { initiative: 'Advanced AI deployment', description: 'Scale generative AI across operations', effort: 'High', impact: 'High', timeline: '12-16 weeks' }
    ];
  };
  
  const quickWins = results.quick_wins?.length > 0 ? results.quick_wins : generateQuickWins();
  quickWins.slice(0, 5).forEach((win, idx) => {
    const y = 1.3 + idx * 0.9;
    
    slide5.addShape('rect', {
      x: 0.5, y: y, w: 9, h: 0.8,
      fill: { color: 'FFFBEB' },
      line: { color: 'FCD34D', width: 1 }
    });
    
    slide5.addText(`⚡ ${win.initiative}`, {
      x: 0.7, y: y + 0.1, w: 6, h: 0.35,
      fontSize: 11, bold: true, color: colors.dark
    });
    slide5.addText(win.description || '', {
      x: 0.7, y: y + 0.4, w: 6, h: 0.3,
      fontSize: 9, color: '6B7280'
    });
    slide5.addText(`${win.effort || 'Low'} Effort | ${win.impact || 'High'} Impact | ${win.timeline || '0-3 months'}`, {
      x: 7, y: y + 0.25, w: 2.3, h: 0.3,
      fontSize: 8, color: colors.primary, align: 'right'
    });
  });

  // ============== Slide 6: Strategic Recommendations ==============
  const slide6 = pptx.addSlide();
  slide6.addText('Strategic Recommendations', {
    x: 0.5, y: 0.3, w: 9, h: 0.6,
    fontSize: 28, bold: true, color: colors.dark
  });
  
  const recommendations = results.strategic_recommendations || [];
  
  // Table header
  const tableData = [
    [
      { text: '#', options: { bold: true, fill: { color: colors.dark }, color: colors.white } },
      { text: 'Initiative', options: { bold: true, fill: { color: colors.dark }, color: colors.white } },
      { text: 'Priority', options: { bold: true, fill: { color: colors.dark }, color: colors.white } },
      { text: 'Timeline', options: { bold: true, fill: { color: colors.dark }, color: colors.white } },
      { text: 'Investment', options: { bold: true, fill: { color: colors.dark }, color: colors.white } }
    ]
  ];
  
  recommendations.slice(0, 6).forEach((rec, idx) => {
    const priorityColor = rec.priority === 'Critical' ? colors.red : 
                          rec.priority === 'High' ? colors.orange : colors.yellow;
    tableData.push([
      { text: String(idx + 1), options: { align: 'center' } },
      { text: rec.initiative || '' },
      { text: rec.priority || '', options: { color: priorityColor, bold: true } },
      { text: rec.timeline || '' },
      { text: rec.investment || '' }
    ]);
  });
  
  slide6.addTable(tableData, {
    x: 0.5, y: 1, w: 9, h: 3.5,
    fontSize: 9,
    border: { pt: 0.5, color: colors.light },
    colW: [0.4, 4, 1.2, 1.8, 1.6]
  });

  // ============== Slide 7: Implementation Roadmap ==============
  const slide7 = pptx.addSlide();
  slide7.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.primary }
  });
  slide7.addText('12-Month Implementation Roadmap', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  // Use score-based roadmap if not provided
  const defaultRoadmap = getDigitalRoadmap(overallScore);
  const roadmap = results.implementation_roadmap?.length > 0 ? results.implementation_roadmap : defaultRoadmap;
  
  const phaseColors = [colors.primary, colors.secondary, '0891B2', colors.green];
  
  roadmap.slice(0, 4).forEach((phase, idx) => {
    const x = 0.5 + idx * 2.4;
    
    // Phase header
    slide7.addShape('rect', {
      x: x, y: 1.1, w: 2.2, h: 0.7,
      fill: { color: phaseColors[idx] || colors.primary }
    });
    slide7.addText(`Phase ${idx + 1}`, {
      x: x, y: 1.15, w: 2.2, h: 0.25,
      fontSize: 9, color: colors.white, align: 'center'
    });
    slide7.addText(phase.phase || `Phase ${idx + 1}`, {
      x: x, y: 1.35, w: 2.2, h: 0.35,
      fontSize: 11, bold: true, color: colors.white, align: 'center'
    });
    
    // Duration
    slide7.addText(phase.duration || '', {
      x: x, y: 1.85, w: 2.2, h: 0.25,
      fontSize: 9, color: phaseColors[idx] || colors.primary, align: 'center'
    });
    
    // Content box
    slide7.addShape('rect', {
      x: x, y: 2.15, w: 2.2, h: 2.8,
      fill: { color: colors.light }
    });
    
    let contentY = 2.25;
    
    // Focus areas
    const focusAreas = phase.focus_areas || [];
    if (focusAreas.length > 0) {
      slide7.addText('Focus:', {
        x: x + 0.1, y: contentY, w: 2, h: 0.25,
        fontSize: 8, bold: true, color: phaseColors[idx] || colors.primary
      });
      contentY += 0.25;
      
      focusAreas.slice(0, 3).forEach(area => {
        const areaText = typeof area === 'string' ? area : String(area);
        slide7.addText(`• ${areaText}`, {
          x: x + 0.1, y: contentY, w: 2, h: 0.35,
          fontSize: 7, color: colors.dark, valign: 'top', wrap: true
        });
        contentY += 0.32;
      });
    }
    
    contentY += 0.1;
    
    // Initiatives
    const initiatives = phase.initiatives || phase.key_initiatives || [];
    if (initiatives.length > 0) {
      slide7.addText('Initiatives:', {
        x: x + 0.1, y: contentY, w: 2, h: 0.25,
        fontSize: 8, bold: true, color: phaseColors[idx] || colors.primary
      });
      contentY += 0.25;
      
      initiatives.slice(0, 3).forEach(init => {
        const initText = typeof init === 'string' ? init : (init.name || String(init));
        const truncatedText = initText.length > 38 ? initText.substring(0, 35) + '...' : initText;
        slide7.addText(`• ${truncatedText}`, {
          x: x + 0.1, y: contentY, w: 2, h: 0.35,
          fontSize: 7, color: colors.mediumGray, valign: 'top', wrap: true
        });
        contentY += 0.32;
      });
    }
    
    // Investment
    if (phase.investment_range) {
      slide7.addShape('rect', {
        x: x + 0.1, y: 4.55, w: 2, h: 0.3,
        fill: { color: phaseColors[idx] || colors.primary }
      });
      slide7.addText(phase.investment_range, {
        x: x + 0.1, y: 4.58, w: 2, h: 0.25,
        fontSize: 8, bold: true, color: colors.white, align: 'center'
      });
    }
    
    // Arrow between phases
    if (idx < 3) {
      slide7.addText('→', {
        x: x + 2.1, y: 1.3, w: 0.4, h: 0.4,
        fontSize: 16, color: colors.secondary, align: 'center'
      });
    }
  });

  // ============== Slide 8: Investment Priorities ==============
  const slide8 = pptx.addSlide();
  slide8.addShape('rect', {
    x: 0, y: 0, w: 10, h: 1,
    fill: { color: colors.secondary }
  });
  slide8.addText('Investment Priorities', {
    x: 0.5, y: 0.3, w: 9, h: 0.5,
    fontSize: 28, bold: true, color: colors.white
  });
  
  // Generate score-specific investment priorities if not provided
  const generateInvestmentPriorities = () => {
    if (overallScore < 2) {
      return [
        { area: 'Cloud Infrastructure', current_state: 'Legacy on-premise', target_state: 'Hybrid cloud', investment_required: '$150K-300K', roi_timeline: '12-18 months' },
        { area: 'Process Automation', current_state: 'Manual processes', target_state: 'RPA deployed', investment_required: '$75K-150K', roi_timeline: '6-12 months' },
        { area: 'Data Platform', current_state: 'Siloed data', target_state: 'Unified analytics', investment_required: '$100K-200K', roi_timeline: '12-18 months' },
        { area: 'Digital Skills', current_state: 'Limited capabilities', target_state: 'Trained workforce', investment_required: '$50K-100K', roi_timeline: '6-9 months' },
        { area: 'Customer Experience', current_state: 'Basic digital', target_state: 'Omnichannel', investment_required: '$100K-250K', roi_timeline: '9-15 months' }
      ];
    } else if (overallScore < 3) {
      return [
        { area: 'AI/ML Platform', current_state: 'Basic analytics', target_state: 'Predictive AI', investment_required: '$200K-400K', roi_timeline: '12-18 months' },
        { area: 'DevOps Maturity', current_state: 'Basic CI/CD', target_state: 'Full DevOps', investment_required: '$100K-200K', roi_timeline: '6-12 months' },
        { area: 'Customer Intelligence', current_state: 'Descriptive', target_state: 'Predictive', investment_required: '$150K-300K', roi_timeline: '9-15 months' },
        { area: 'Innovation Lab', current_state: 'Ad-hoc', target_state: 'Structured', investment_required: '$75K-150K', roi_timeline: '6-12 months' },
        { area: 'Digital Products', current_state: 'Internal focus', target_state: 'Market offerings', investment_required: '$200K-500K', roi_timeline: '12-24 months' }
      ];
    }
    return [
      { area: 'Edge AI', current_state: 'Cloud-only AI', target_state: 'Edge deployment', investment_required: '$300K-600K', roi_timeline: '12-18 months' },
      { area: 'Platform Business', current_state: 'Product-centric', target_state: 'Platform model', investment_required: '$400K-800K', roi_timeline: '18-24 months' },
      { area: 'Ecosystem Integration', current_state: 'Limited partners', target_state: 'Full ecosystem', investment_required: '$200K-400K', roi_timeline: '12-18 months' },
      { area: 'Advanced Analytics', current_state: 'Predictive', target_state: 'Prescriptive AI', investment_required: '$250K-500K', roi_timeline: '12-18 months' },
      { area: 'Digital Workforce', current_state: 'Skilled team', target_state: 'Industry leaders', investment_required: '$150K-300K', roi_timeline: '6-12 months' }
    ];
  };
  
  const investments = results.investment_priorities?.length > 0 ? results.investment_priorities : generateInvestmentPriorities();
  investments.slice(0, 5).forEach((inv, idx) => {
    const y = 1.15 + idx * 0.85;
    
    slide8.addShape('rect', {
      x: 0.5, y: y, w: 9, h: 0.75,
      fill: { color: colors.white },
      line: { color: colors.light, width: 1 }
    });
    
    slide8.addText(`${idx + 1}. ${inv.area || ''}`, {
      x: 0.7, y: y + 0.1, w: 4, h: 0.3,
      fontSize: 11, bold: true, color: colors.dark
    });
    
    slide8.addText(`Current: ${inv.current_state || 'N/A'} → Target: ${inv.target_state || 'N/A'}`, {
      x: 0.7, y: y + 0.4, w: 5, h: 0.25,
      fontSize: 9, color: '6B7280'
    });
    
    slide8.addText(inv.investment_required || '', {
      x: 6, y: y + 0.15, w: 1.8, h: 0.25,
      fontSize: 10, bold: true, color: colors.primary, align: 'right'
    });
    
    slide8.addText(`ROI: ${inv.roi_timeline || 'N/A'}`, {
      x: 6, y: y + 0.4, w: 1.8, h: 0.25,
      fontSize: 9, color: '6B7280', align: 'right'
    });
  });

  // ============== Slide 9: Industry Benchmark ==============
  const slide9 = pptx.addSlide();
  slide9.addText('Industry Benchmark Comparison', {
    x: 0.5, y: 0.3, w: 9, h: 0.6,
    fontSize: 28, bold: true, color: colors.dark
  });
  
  const benchmarkData = results.benchmark_comparison || benchmark || {};
  
  // Benchmark cards
  const benchmarks = [
    { label: 'Your Score', value: overallScore.toFixed(1), color: getScoreColor(overallScore) },
    { label: 'Industry Average', value: benchmarkData.industry_average ?? 'Not available', color: '6B7280' },
    { label: 'Top Performers', value: benchmarkData.top_performers ?? 'Not available', color: colors.primary }
  ];
  
  benchmarks.forEach((b, idx) => {
    const x = 0.5 + idx * 3.2;
    
    slide9.addShape('rect', {
      x: x, y: 1.2, w: 3, h: 2,
      fill: { color: colors.light }
    });
    
    slide9.addText(b.label, {
      x: x, y: 1.4, w: 3, h: 0.4,
      fontSize: 12, color: '6B7280', align: 'center'
    });
    
    slide9.addText(String(b.value), {
      x: x, y: 1.9, w: 3, h: 0.8,
      fontSize: 36, bold: true, color: b.color, align: 'center'
    });
  });
  
  // Competitive position
  if (benchmarkData.competitive_position) {
    slide9.addText('Competitive Position:', {
      x: 0.5, y: 3.5, w: 9, h: 0.4,
      fontSize: 12, bold: true, color: colors.dark
    });
    slide9.addText(benchmarkData.competitive_position, {
      x: 0.5, y: 3.9, w: 9, h: 1,
      fontSize: 11, color: colors.dark
    });
  }

  // ============== Slide 10: Level Progression - Journey to Next Level ==============
  const levelProgression = data.ai_analysis?.level_progression;
  if (levelProgression) {
    const slideLP = pptx.addSlide();
    
    // Header
    slideLP.addShape('rect', {
      x: 0, y: 0, w: 10, h: 1.1,
      fill: { color: colors.primary }
    });
    slideLP.addText('Level Up: Journey to Next Digital Maturity Level', {
      x: 0.5, y: 0.2, w: 9, h: 0.4,
      fontSize: 24, bold: true, color: colors.white
    });
    slideLP.addText('Time, Effort, Investment & Benefits Analysis', {
      x: 0.5, y: 0.6, w: 9, h: 0.3,
      fontSize: 11, color: 'FFFFFF'
    });

    // Current to Next Level Visual
    const currentLevel = levelProgression.current_level?.level_number || Math.floor(overallScore);
    const nextLevel = levelProgression.next_level?.level_number || Math.min(currentLevel + 1, 5);
    const currentLevelName = levelProgression.current_level?.level_name || data.overall_maturity_level || 'Current';
    const nextLevelName = levelProgression.next_level?.level_name || 'Next Level';
    
    // Current Level Circle
    slideLP.addShape('ellipse', {
      x: 1.5, y: 1.3, w: 1.2, h: 1.2,
      fill: { color: colors.orange }
    });
    slideLP.addText(String(currentLevel), {
      x: 1.5, y: 1.55, w: 1.2, h: 0.7,
      fontSize: 28, bold: true, color: colors.white, align: 'center'
    });
    slideLP.addText('Current', {
      x: 1.2, y: 2.5, w: 1.8, h: 0.3,
      fontSize: 10, bold: true, color: colors.darkGray, align: 'center'
    });
    slideLP.addText(currentLevelName, {
      x: 1, y: 2.75, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.mediumGray, align: 'center'
    });

    // Arrow with time
    slideLP.addShape('rect', {
      x: 3.2, y: 1.75, w: 3.6, h: 0.3,
      fill: { color: colors.primary }
    });
    slideLP.addText(levelProgression.progression_requirements?.time_estimate || '6-12 months', {
      x: 3.2, y: 2.1, w: 3.6, h: 0.3,
      fontSize: 10, bold: true, color: colors.primary, align: 'center'
    });

    // Next Level Circle
    slideLP.addShape('ellipse', {
      x: 7.3, y: 1.3, w: 1.2, h: 1.2,
      fill: { color: colors.primary }
    });
    slideLP.addText(String(nextLevel), {
      x: 7.3, y: 1.55, w: 1.2, h: 0.7,
      fontSize: 28, bold: true, color: colors.white, align: 'center'
    });
    slideLP.addText('Target', {
      x: 7, y: 2.5, w: 1.8, h: 0.3,
      fontSize: 10, bold: true, color: colors.darkGray, align: 'center'
    });
    slideLP.addText(nextLevelName, {
      x: 6.8, y: 2.75, w: 2.2, h: 0.3,
      fontSize: 9, color: colors.primary, align: 'center'
    });

    // Resource Requirements Grid
    const reqY = 3.2;
    const reqCards = [
      { label: 'TIME', value: levelProgression.progression_requirements?.time_estimate || '6-12 months', color: '3B82F6' },
      { label: 'MANPOWER', value: levelProgression.progression_requirements?.manpower_resources?.internal_fte || 'Not available', color: '8B5CF6' },
      { label: 'INVESTMENT', value: levelProgression.progression_requirements?.investment_required?.total_range || 'Not available', color: colors.primary },
      { label: 'ROI', value: levelProgression.progression_requirements?.business_benefits?.quantified_value || 'Not available', color: colors.orange }
    ];

    reqCards.forEach((card, idx) => {
      const x = 0.5 + idx * 2.4;
      
      slideLP.addShape('rect', {
        x: x, y: reqY, w: 2.2, h: 1.2,
        fill: { color: colors.light }
      });
      
      slideLP.addShape('rect', {
        x: x, y: reqY, w: 2.2, h: 0.35,
        fill: { color: card.color }
      });
      slideLP.addText(card.label, {
        x: x, y: reqY + 0.05, w: 2.2, h: 0.25,
        fontSize: 9, bold: true, color: colors.white, align: 'center'
      });
      
      slideLP.addText(card.value, {
        x: x + 0.1, y: reqY + 0.45, w: 2, h: 0.65,
        fontSize: 11, bold: true, color: card.color, align: 'center', valign: 'middle'
      });
    });

    // Business Benefits Section
    const benY = 4.6;
    slideLP.addText('Business Benefits at Next Level', {
      x: 0.5, y: benY, w: 9, h: 0.35,
      fontSize: 12, bold: true, color: colors.primary
    });

    const benefits = [
      { label: 'Efficiency Gains', value: levelProgression.progression_requirements?.business_benefits?.efficiency_gains || 'Not available' },
      { label: 'Revenue Impact', value: levelProgression.progression_requirements?.business_benefits?.revenue_impact || 'Not available' },
      { label: 'Risk Reduction', value: levelProgression.progression_requirements?.business_benefits?.risk_reduction || 'Not available' }
    ];

    benefits.forEach((ben, idx) => {
      const x = 0.5 + idx * 3.1;
      slideLP.addShape('rect', {
        x: x, y: benY + 0.4, w: 2.9, h: 0.7,
        fill: { color: 'ECFDF5' },
        line: { color: colors.primary, width: 1 }
      });
      slideLP.addText(ben.label, {
        x: x + 0.1, y: benY + 0.45, w: 2.7, h: 0.25,
        fontSize: 8, color: colors.mediumGray
      });
      slideLP.addText(ben.value, {
        x: x + 0.1, y: benY + 0.7, w: 2.7, h: 0.35,
        fontSize: 10, bold: true, color: colors.primary
      });
    });
  }

  // ============== Slide 11 (or 10 if no level progression): Thank You ==============
  const slideThankYou = pptx.addSlide();
  slideThankYou.background = { color: colors.dark };
  
  slideThankYou.addText('Thank You', {
    x: 0.5, y: 2, w: 9, h: 1,
    fontSize: 44, bold: true, color: colors.white, align: 'center'
  });
  
  slideThankYou.addText('For questions about this assessment or next steps, contact us:', {
    x: 0.5, y: 3.2, w: 9, h: 0.5,
    fontSize: 14, color: 'A0AEC0', align: 'center'
  });
  
  slideThankYou.addText('www.samanvai.com', {
    x: 0.5, y: 3.8, w: 9, h: 0.5,
    fontSize: 16, color: colors.primary, align: 'center'
  });
  
  slideThankYou.addText('SAMANVAI - Agentic AI for CXOs', {
    x: 0.5, y: 4.8, w: 9, h: 0.4,
    fontSize: 12, color: '718096', align: 'center'
  });

  // Download the file
  const companyName = (data.company_name || results.company_name || 'Company').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const fileName = `Digital_Maturity_Assessment_${companyName}_${new Date().toISOString().split('T')[0]}`;
  appendQualitySlide(pptx, fullResults || results);
  return pptx.writeFile({ fileName: fileName });
};

export default generateMaturityPptx;
